import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Pause, Play, RotateCcw } from "lucide-react";

interface BreathingSessionProps {
  onClose: () => void;
}

type Phase = "inhale" | "hold" | "exhale";

const phaseDurations = {
  inhale: 4,
  hold: 2,
  exhale: 6,
};

const phaseColors = {
  inhale: "from-blue-400 to-cyan-400",
  hold: "from-purple-400 to-indigo-400",
  exhale: "from-pink-400 to-rose-400",
};

const phaseText = {
  inhale: "Breathe In",
  hold: "Hold",
  exhale: "Breathe Out",
};

export const BreathingSession = ({ onClose }: BreathingSessionProps) => {
  const [phase, setPhase] = useState<Phase>("inhale");
  const [countdown, setCountdown] = useState(phaseDurations.inhale);
  const [cycleCount, setcycleCount] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [totalCycles] = useState(10);

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Move to next phase
          let nextPhase: Phase;
          if (phase === "inhale") {
            nextPhase = "hold";
          } else if (phase === "hold") {
            nextPhase = "exhale";
          } else {
            nextPhase = "inhale";
            setcycleCount((c) => c + 1);
          }
          setPhase(nextPhase);
          return phaseDurations[nextPhase];
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase, isActive]);

  const togglePause = () => {
    setIsActive(!isActive);
  };

  const reset = () => {
    setPhase("inhale");
    setCountdown(phaseDurations.inhale);
    setcycleCount(0);
    setIsActive(false); // Pause after reset so user can start when ready
  };

  const progress = (cycleCount / totalCycles) * 100;
  const isComplete = cycleCount >= totalCycles;

  // Calculate circle scale based on phase
  const getCircleScale = () => {
    const elapsed = phaseDurations[phase] - countdown;
    const duration = phaseDurations[phase];
    const progress = elapsed / duration;

    if (phase === "inhale") {
      return 0.5 + (progress * 0.5); // Scale from 50% to 100%
    } else if (phase === "exhale") {
      return 1 - (progress * 0.5); // Scale from 100% to 50%
    }
    return 1; // Hold stays at 100%
  };

  if (isComplete) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-12 text-center relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4"
          >
            <X className="h-5 w-5" />
          </Button>

          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 flex items-center justify-center">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold mb-4 text-foreground">Session Complete!</h2>
            <p className="text-lg text-muted-foreground mb-2">
              You completed {cycleCount} breathing cycles
            </p>
            <p className="text-muted-foreground">
              Great job taking time for yourself. Notice how you feel now.
            </p>
          </div>

          <div className="flex gap-3 justify-center">
            <Button onClick={reset} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Start Again
            </Button>
            <Button variant="outline" onClick={onClose}>
              Back to Toolkit
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <Button
          variant="ghost"
          onClick={onClose}
          className="mb-6 gap-2"
        >
          <X className="h-4 w-4" />
          Exit Session
        </Button>

        <Card className="p-8 md:p-12">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-muted-foreground mb-2">
              <span>Cycle {cycleCount} of {totalCycles}</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Breathing Circle */}
          <div className="flex justify-center mb-12">
            <div className="relative w-80 h-80 flex items-center justify-center">
              {/* Outer rings */}
              <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
              <div className="absolute inset-4 rounded-full border-2 border-primary/10" />
              
              {/* Main breathing circle */}
              <div 
                className={`absolute rounded-full bg-gradient-to-r ${phaseColors[phase]} transition-all duration-1000 ease-in-out flex items-center justify-center`}
                style={{
                  width: `${getCircleScale() * 100}%`,
                  height: `${getCircleScale() * 100}%`,
                  boxShadow: `0 0 ${getCircleScale() * 60}px ${getCircleScale() * 20}px rgba(59, 130, 246, 0.3)`
                }}
              >
                <div className="text-center text-white">
                  <div className="text-6xl font-bold mb-2">{countdown}</div>
                  <div className="text-xl font-medium opacity-90">{phaseText[phase]}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex justify-center gap-3">
            <Button onClick={togglePause} variant="outline" className="gap-2">
              {isActive ? (
                <>
                  <Pause className="h-4 w-4" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Resume
                </>
              )}
            </Button>
            <Button onClick={reset} variant="outline" className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};
